/**
 * Shared utilities for embed configuration settings
 * Handles localStorage persistence for settings that need to survive navigation within iframe
 */

// localStorage keys
const EMBED_SHOW_ALL_BUTTONS_KEY = 'embed-show-all-buttons';
const EMBED_PLAN_MOVES_SETTINGS_KEY = 'embed-plan-moves-settings';
const EMBED_BRANDING_SETTINGS_KEY = 'embed-branding-settings';
const EMBED_HIDDEN_BUTTONS_KEY = 'embed-hidden-buttons';

/** The Valet header buttons `hiddenButtons` can hide. */
export const DASHBOARD_BUTTONS = ['planMoves', 'importMoves', 'drivers'] as const;
export type DashboardButton = (typeof DASHBOARD_BUTTONS)[number];

/** The dashboard knobs that shape the Valet header and the Orders tab, read from the URL only. */
export interface DashboardKnobs {
  showToolbar: boolean;
  showFilters: boolean;
  showMetrics: boolean;
  compactMode: boolean;
}

/** What each dashboard knob reads when the URL does not set it. */
export const DASHBOARD_KNOB_DEFAULTS: DashboardKnobs = {
  showToolbar: true,
  showFilters: true,
  showMetrics: true,
  compactMode: false,
};

// Type for branding settings stored in localStorage
export interface BrandingSettings {
  primaryColor?: string;
  fontFamily?: string;
  unassignedDriverButtonColor?: string;
}

// Valid move types
const VALID_MOVE_TYPES = ['concierge', 'concierge-loaner', 'one-way', 'round-trip'];

// Type for plan-moves settings stored in localStorage
export interface PlanMovesSettings {
  type?: string;
  skipTypeSelection?: boolean;
  lockType?: boolean;
  lockConsumerAction?: boolean;
  lockTime?: boolean;
  lockLoanerVin?: boolean;
  typeOverrides?: {
    concierge?: { label?: string; description?: string };
    'concierge-loaner'?: { label?: string; description?: string };
    'one-way'?: { label?: string; description?: string };
    'round-trip'?: { label?: string; description?: string };
  };
}

/**
 * Get showAllButtons preference, checking URL first then localStorage
 * When URL param is present, it updates localStorage for future navigations
 *
 * @param searchParams - URLSearchParams from the current URL
 * @returns boolean - whether to show navigation buttons (default true)
 */
export function getShowAllButtons(searchParams: URLSearchParams): boolean {
  const urlParam = searchParams.get('showAllButtons');

  // If URL param exists, use it and persist to localStorage
  if (urlParam !== null) {
    const showAll = urlParam !== 'false';
    try {
      localStorage.setItem(EMBED_SHOW_ALL_BUTTONS_KEY, JSON.stringify(showAll));
    } catch (e) {
      console.warn('Failed to save showAllButtons to localStorage:', e);
    }
    return showAll;
  }

  // Otherwise, try to read from localStorage
  try {
    const stored = localStorage.getItem(EMBED_SHOW_ALL_BUTTONS_KEY);
    if (stored !== null) {
      return JSON.parse(stored) === true;
    }
  } catch (e) {
    console.warn('Failed to read showAllButtons from localStorage:', e);
  }

  // Default to true (show all buttons)
  return true;
}

const isDashboardButton = (value: unknown): value is DashboardButton => DASHBOARD_BUTTONS.includes(value as DashboardButton);

/** The comma-separated `hiddenButtons` value as known button keys; unknown keys are dropped. */
function parseHiddenButtons(value: string): DashboardButton[] {
  return value
    .split(',')
    .map(button => button.trim())
    .filter(isDashboardButton);
}

/**
 * The Valet header buttons to hide, checking the URL first then localStorage. A URL value
 * (even an empty one, which hides nothing) is persisted, so the pages reached afterwards keep it.
 */
export function getHiddenButtons(searchParams: URLSearchParams): DashboardButton[] {
  const urlParam = searchParams.get('hiddenButtons');
  if (urlParam === null) return readHiddenButtons(searchParams);

  const buttons = parseHiddenButtons(urlParam);
  try {
    localStorage.setItem(EMBED_HIDDEN_BUTTONS_KEY, JSON.stringify(buttons));
  } catch (e) {
    console.warn('Failed to save hiddenButtons to localStorage:', e);
  }
  return buttons;
}

/** The Valet header buttons to hide, as `getHiddenButtons` reads them, without persisting: for a
 * render, where the Layout's persist effect does the saving. */
export function readHiddenButtons(searchParams: URLSearchParams): DashboardButton[] {
  const urlParam = searchParams.get('hiddenButtons');
  if (urlParam !== null) return parseHiddenButtons(urlParam);

  try {
    const stored = localStorage.getItem(EMBED_HIDDEN_BUTTONS_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed.filter(isDashboardButton);
    }
  } catch (e) {
    console.warn('Failed to read hiddenButtons from localStorage:', e);
  }

  return [];
}

/** A boolean knob off its default only when the URL says the opposite word: a knob that defaults
 * on turns off only on `false`, one that defaults off turns on only on `true`. */
function readKnob(searchParams: URLSearchParams, key: keyof DashboardKnobs): boolean {
  const fallback = DASHBOARD_KNOB_DEFAULTS[key];
  return searchParams.get(key) === String(!fallback) ? !fallback : fallback;
}

/** The dashboard knobs, from the URL only (no persistence). */
export function getDashboardKnobs(searchParams: URLSearchParams): DashboardKnobs {
  return {
    showToolbar: readKnob(searchParams, 'showToolbar'),
    showFilters: readKnob(searchParams, 'showFilters'),
    showMetrics: readKnob(searchParams, 'showMetrics'),
    compactMode: readKnob(searchParams, 'compactMode'),
  };
}

/** Persist the button settings (`showAllButtons`, `hiddenButtons`) a URL carries, so the pages
 * reached by internal navigation read them. */
export function persistButtonSettings(searchParams: URLSearchParams): void {
  getShowAllButtons(searchParams);
  getHiddenButtons(searchParams);
}

/**
 * Persist all plan-moves related settings from URL params to localStorage.
 * This should be called on every page load so that when users navigate
 * internally (e.g., clicking "Plan Moves" button on dashboard), the settings
 * are already saved and can be read by `features/moveCreation/plannerEmbedInputs.ts`.
 *
 * @param searchParams - URLSearchParams from the current URL
 */
export function persistPlanMovesSettings(searchParams: URLSearchParams): void {
  const settings: PlanMovesSettings = {};
  let hasSettings = false;

  // Check for type parameter
  const typeParam = searchParams.get('type');
  if (typeParam && VALID_MOVE_TYPES.includes(typeParam)) {
    settings.type = typeParam;
    hasSettings = true;
  }

  // Check for skipTypeSelection parameter (only valid when type is also set)
  const skipTypeSelectionParam = searchParams.get('skipTypeSelection');
  if (skipTypeSelectionParam === 'true' && settings.type) {
    settings.skipTypeSelection = true;
    hasSettings = true;
  }

  // Check for lock parameters
  const lockTypeParam = searchParams.get('lockType');
  if (lockTypeParam === 'true' && settings.type) {
    settings.lockType = true;
    hasSettings = true;
  }

  const lockConsumerActionParam = searchParams.get('lockConsumerAction');
  if (lockConsumerActionParam === 'true') {
    settings.lockConsumerAction = true;
    hasSettings = true;
  }

  const lockTimeParam = searchParams.get('lockTime');
  if (lockTimeParam === 'true') {
    settings.lockTime = true;
    hasSettings = true;
  }

  const lockLoanerVinParam = searchParams.get('lockLoanerVin');
  if (lockLoanerVinParam === 'true') {
    settings.lockLoanerVin = true;
    hasSettings = true;
  }

  // Check for type override parameters
  const typeOverrides: PlanMovesSettings['typeOverrides'] = {};
  let hasTypeOverrides = false;

  // Concierge overrides
  const conciergeLabel = searchParams.get('conciergeLabel');
  const conciergeDesc = searchParams.get('conciergeDesc');
  if (conciergeLabel || conciergeDesc) {
    typeOverrides.concierge = {};
    if (conciergeLabel) typeOverrides.concierge.label = conciergeLabel;
    if (conciergeDesc) typeOverrides.concierge.description = conciergeDesc;
    hasTypeOverrides = true;
  }

  // Concierge + Loaner overrides
  const conciergeLoanerLabel = searchParams.get('conciergeLoanerLabel');
  const conciergeLoanerDesc = searchParams.get('conciergeLoanerDesc');
  if (conciergeLoanerLabel || conciergeLoanerDesc) {
    typeOverrides['concierge-loaner'] = {};
    if (conciergeLoanerLabel) typeOverrides['concierge-loaner'].label = conciergeLoanerLabel;
    if (conciergeLoanerDesc) typeOverrides['concierge-loaner'].description = conciergeLoanerDesc;
    hasTypeOverrides = true;
  }

  // One-Way overrides
  const oneWayLabel = searchParams.get('oneWayLabel');
  const oneWayDesc = searchParams.get('oneWayDesc');
  if (oneWayLabel || oneWayDesc) {
    typeOverrides['one-way'] = {};
    if (oneWayLabel) typeOverrides['one-way'].label = oneWayLabel;
    if (oneWayDesc) typeOverrides['one-way'].description = oneWayDesc;
    hasTypeOverrides = true;
  }

  // Round-Trip overrides
  const roundTripLabel = searchParams.get('roundTripLabel');
  const roundTripDesc = searchParams.get('roundTripDesc');
  if (roundTripLabel || roundTripDesc) {
    typeOverrides['round-trip'] = {};
    if (roundTripLabel) typeOverrides['round-trip'].label = roundTripLabel;
    if (roundTripDesc) typeOverrides['round-trip'].description = roundTripDesc;
    hasTypeOverrides = true;
  }

  if (hasTypeOverrides) {
    settings.typeOverrides = typeOverrides;
    hasSettings = true;
  }

  // Only save if we have at least one setting from URL params
  if (hasSettings) {
    try {
      localStorage.setItem(EMBED_PLAN_MOVES_SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save plan-moves settings to localStorage:', e);
    }
  }
}

/**
 * Save the integrator's move-type wording beside the other plan-moves settings,
 * so navigation inside the iframe keeps it.
 */
export function persistPlanMovesTypeOverrides(typeOverrides: PlanMovesSettings['typeOverrides']): void {
  try {
    const existing = JSON.parse(localStorage.getItem(EMBED_PLAN_MOVES_SETTINGS_KEY) || '{}');
    localStorage.setItem(EMBED_PLAN_MOVES_SETTINGS_KEY, JSON.stringify({ ...existing, typeOverrides }));
  } catch (e) {
    console.warn('Failed to save typeOverrides to localStorage:', e);
  }
}

/**
 * Get plan-moves settings from localStorage.
 * Read by `features/moveCreation/plannerEmbedInputs.ts` when URL params aren't present.
 *
 * @returns PlanMovesSettings or null if not found
 */
export function getPlanMovesSettings(): PlanMovesSettings | null {
  try {
    const stored = localStorage.getItem(EMBED_PLAN_MOVES_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored) as PlanMovesSettings;
    }
  } catch (e) {
    console.warn('Failed to read plan-moves settings from localStorage:', e);
  }
  return null;
}

/**
 * Get the move type setting, checking URL first then localStorage.
 *
 * @param searchParams - URLSearchParams from the current URL
 * @returns string or null if not set
 */
export function getMoveType(searchParams: URLSearchParams): string | null {
  const urlParam = searchParams.get('type');
  if (urlParam && VALID_MOVE_TYPES.includes(urlParam)) {
    return urlParam;
  }

  const stored = getPlanMovesSettings();
  if (stored?.type && VALID_MOVE_TYPES.includes(stored.type)) {
    return stored.type;
  }

  return null;
}

/**
 * Get the skipTypeSelection setting, checking URL first then localStorage.
 * Only returns true if a valid type is also set.
 *
 * @param searchParams - URLSearchParams from the current URL
 * @returns boolean
 */
export function getSkipTypeSelection(searchParams: URLSearchParams): boolean {
  const moveType = getMoveType(searchParams);
  if (!moveType) return false;

  const urlParam = searchParams.get('skipTypeSelection');
  if (urlParam === 'true') {
    return true;
  }

  const stored = getPlanMovesSettings();
  return stored?.skipTypeSelection === true;
}

/**
 * Persist branding settings (primaryColor, fontFamily) from URL params to localStorage.
 * This should be called on every page load so that branding settings are available
 * for all pages and survive internal navigation.
 *
 * @param searchParams - URLSearchParams from the current URL
 */
export function persistBrandingSettings(searchParams: URLSearchParams): void {
  // Merge with existing so params not in current URL are preserved (e.g. unassignedDriverButtonColor when on dashboard)
  const existing = getBrandingSettings();
  const settings: BrandingSettings = { ...existing };

  let hasSettings = false;

  // Check for primaryColor parameter
  const primaryColorParam = searchParams.get('primaryColor');
  if (primaryColorParam) {
    settings.primaryColor = primaryColorParam;
    hasSettings = true;
  }

  // Check for fontFamily parameter
  const fontFamilyParam = searchParams.get('fontFamily');
  if (fontFamilyParam) {
    settings.fontFamily = fontFamilyParam;
    hasSettings = true;
  }

  // Check for unassignedDriverButtonColor parameter (move details: Assign Driver button)
  const unassignedDriverButtonColorParam = searchParams.get('unassignedDriverButtonColor');
  if (unassignedDriverButtonColorParam) {
    settings.unassignedDriverButtonColor = unassignedDriverButtonColorParam;
    hasSettings = true;
  }

  // Save when we have any branding param in URL (merge preserves others)
  if (hasSettings) {
    try {
      localStorage.setItem(EMBED_BRANDING_SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save branding settings to localStorage:', e);
    }
  }
}

/**
 * Get branding settings from localStorage.
 *
 * @returns BrandingSettings or null if not found
 */
export function getBrandingSettings(): BrandingSettings | null {
  try {
    const stored = localStorage.getItem(EMBED_BRANDING_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored) as BrandingSettings;
    }
  } catch (e) {
    console.warn('Failed to read branding settings from localStorage:', e);
  }
  return null;
}

/**
 * Get the fontFamily setting, checking URL first then localStorage.
 *
 * @param searchParams - URLSearchParams from the current URL
 * @returns string or undefined if not set
 */
export function getFontFamily(searchParams: URLSearchParams): string | undefined {
  const urlParam = searchParams.get('fontFamily');
  if (urlParam) {
    return urlParam;
  }

  const stored = getBrandingSettings();
  return stored?.fontFamily || undefined;
}

/**
 * Get the primaryColor setting, checking URL first then localStorage.
 *
 * @param searchParams - URLSearchParams from the current URL
 * @returns string or undefined if not set
 */
export function getPrimaryColor(searchParams: URLSearchParams): string | undefined {
  const urlParam = searchParams.get('primaryColor');
  if (urlParam) {
    return urlParam;
  }

  const stored = getBrandingSettings();
  return stored?.primaryColor || undefined;
}

/**
 * Get the unassignedDriverButtonColor setting, checking URL first then localStorage.
 * Used on the move details screen for the "Assign Driver" button when no driver is assigned.
 *
 * @param searchParams - URLSearchParams from the current URL
 * @returns string or undefined if not set
 */
export function getUnassignedDriverButtonColor(searchParams: URLSearchParams): string | undefined {
  const urlParam = searchParams.get('unassignedDriverButtonColor');
  const raw = urlParam || getBrandingSettings()?.unassignedDriverButtonColor;
  if (!raw) return undefined;
  // Normalize hex so browser applies it (e.g. "1a5f7a" -> "#1a5f7a")
  return raw.startsWith('#') ? raw : `#${raw}`;
}

/**
 * Get the lockType setting, checking URL first then localStorage.
 * Only returns true if a valid type is also set.
 */
export function getLockType(searchParams: URLSearchParams): boolean {
  const moveType = getMoveType(searchParams);
  if (!moveType) return false;

  if (searchParams.get('lockType') === 'true') return true;

  return getPlanMovesSettings()?.lockType === true;
}

/**
 * Get the lockConsumerAction setting, checking URL first then localStorage.
 */
export function getLockConsumerAction(searchParams: URLSearchParams): boolean {
  if (searchParams.get('lockConsumerAction') === 'true') return true;

  return getPlanMovesSettings()?.lockConsumerAction === true;
}

/**
 * Get the lockTime setting, checking URL first then localStorage.
 */
export function getLockTime(searchParams: URLSearchParams): boolean {
  if (searchParams.get('lockTime') === 'true') return true;

  return getPlanMovesSettings()?.lockTime === true;
}

/**
 * Get the lockLoanerVin setting, checking URL first then localStorage.
 * When true, the VIN field is disabled for loaner vehicles in the move planner.
 */
export function getLockLoanerVin(searchParams: URLSearchParams): boolean {
  if (searchParams.get('lockLoanerVin') === 'true') return true;

  return getPlanMovesSettings()?.lockLoanerVin === true;
}

/**
 * Get the readOnly setting directly from the URL query parameter.
 * When true, all edit/action controls are hidden across pages.
 * This is intentionally URL-only (no localStorage) — the embed integrator
 * must pass ?readOnly=true in the iframe src for it to take effect.
 *
 * @param searchParams - URLSearchParams from the current URL
 * @returns boolean
 */
export function getReadOnly(searchParams: URLSearchParams): boolean {
  return searchParams.get('readOnly') === 'true';
}
